<template>
  <section class="dxf-project-portfolio">
    <div class="dxf-project-portfolio__hero lp-hero lp-hero--bg-image" :style="{'background-position': '50% 50%', 'background-image': 'url('+ primaryImage.url +')'}">
      <div class="lp-hero__overlay" style="opacity: 0"> </div>
      <div class="lp-hero__content">
        <div class="lp-hero__content-title-section">
          <div v-if="helperText && helperText != ''" class="lp-hero__content-secondary-title">
            <h3>
              <span class="highlight">
                {{helperText}}
              </span>
            </h3>
          </div>
          <div v-if="title && title != ''" class="lp-hero__content-primary-title">
            <h2>
              <span class="highlight">
                {{title}}
              </span>
            </h2>
          </div>
        </div>
      </div>
    </div>

    <div class="dxf-project-portfolio__description">
      <div class="dxf-project-portfolio__detail-body" v-html="description"></div>
      <div v-if="filters.concat(attributes).length > 0" class="card dxf-project-portfolio__attribute-card">
        <div class="card-body">
          <div v-for="attribute in filters.concat(attributes)" class="dxf-project-portfolio__attribute-card-cell">
            <p class="dxf-project-portfolio__attribute-card-cell-title">{{attribute.title}}</p>
            <p class="dxf-project-portfolio__attribute-card-cell-value">{{attribute.value}}</p>
          </div>
        </div>
      </div>
    </div>

    <div v-if="galleryImages.length > 0" class="dxf-project-portfolio__slider">
      <div class="swiper-container slider">
        <div class="swiper-wrapper">
          <div v-for="image in galleryImages" class="swiper-slide">
            <img :src="image.url" :alt="image.alt">
          </div>
        </div>
        <div class="swiper-button-next">
          <i class="fal fa-chevron-right"></i>
        </div>
        <div class="swiper-button-prev">
          <i class="fal fa-chevron-left"></i>
        </div>
      </div>
      <div class="swiper-container slider-thumbnail">
        <div class="swiper-wrapper">
          <div v-for="image in galleryImages" class="swiper-slide">
            <img :src="image.url" :alt="image.alt">
          </div>
        </div>
      </div>
    </div>

    <div v-if="additionalContent && additionalContent != ''" class="dxf-project-portfolio__additional-content">
      <div class="dxf-project-portfolio__additional-content-body" v-html="additionalContent"></div>
    </div>

    <div v-if="relatedResources.length > 0" class="dxf-project-portfolio__related-resources">
      <div class="dxf-project-portfolio__related-resources-container">
        <div class="dxf-project-portfolio__related-resources-title">
          <h2>Related Resources</h2>
        </div>
        <div class="dxf-project-portfolio__related-resources-body">
          <div class="dxf-project-portfolio__related-resources-body-col-1">
            <div v-for="(relatedResource, index) in relatedResources">
              <a class="card card--horizontal text-decoration-none mb-4" v-if="index % 2 == 0" :href="relatedResource.url">
                <img v-if="!!relatedResource.img" :src="relatedResource.img.url" class="card-img-top card--hover-grow" :alt="relatedResource.img.alt" />
                <div class="card-body">
                  <h3 class="card-title">{{relatedResource.title}}</h3>
                  <small v-if="relatedResource.author || relatedResource.date" class="card-subtitle">{{relatedResource.author ? relatedResource.author : relatedResource.date? formatDate(relatedResource.date) : ""}}</small>
                  <p v-if="relatedResource.summary" class="card-text">
                    {{relatedResource.summary}}
                  </p>
                </div>
              </a>
            </div>
          </div>
          <div class="dxf-project-portfolio__related-resources-body-col-2">
            <div v-for="(relatedResource, index) in relatedResources">
              <a class="card card--horizontal text-decoration-none mb-4" v-if="index % 2 == 1" :href="relatedResource.url">
                <img v-if="!!relatedResource.img" :src="relatedResource.img.url" class="card-img-top card--hover-grow" :alt="relatedResource.img.alt" />
                <div class="card-body">
                  <h3 class="card-title">{{relatedResource.title}}</h3>
                  <small v-if="relatedResource.author || relatedResource.date" class="card-subtitle">{{relatedResource.author ? relatedResource.author : relatedResource.date? formatDate(relatedResource.date) : ""}}</small>
                  <p v-if="relatedResource.summary" class="card-text">
                    {{relatedResource.summary}}
                  </p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script lang="ts">
  import { onMounted, ref } from "vue";
  import Swiper, { Navigation, Pagination, Scrollbar, Thumbs } from 'swiper';

  interface IImage {
    url: string,
    alter: string
  }

  interface IAttribute {
    title: string,
    value: string
  }

  interface IRelatedResource {
    title: string,
    author?: string,
    date?: string,
    summary?: string,
    img?: IImage,
    url: string
  }

  interface IProjectPortfolioDetailProps {
    title: string,
    summary: string,
    desription: string,
    additionalContent: string,
    helperText: string,
    primaryImage: IImage,
    galleryImages: IImage[],
    attributes: IAttribute[],
    filters: IAttribute[],
    relatedResources: IRelatedResource[]
  }

  export default {
    name: 'ProjectPortfolioDetail',
    props: {
      title: {
        type: String,
        required: true,
      },
      summary: {
        type: String,
        required: true,
      },
      description: {
        type: String,
        required: true,
      },
      additionalContent: {
        type: String,
        required: true,
      },
      helperText: {
        type: String,
        required: true
      },
      primaryImage: {
        type: Object,
        required: true
      },
      galleryImages: {
        type: Array,
        required: true
      },
      attributes: {
        type: Object,
        required: true
      },
      filters: {
        type: Object,
        required: true
      },
      relatedResources: {
        type: Array,
        required: true
      }
    },
    setup(props: IProjectPortfolioDetailProps) {
      console.log("PORTFOLIO DETAIL PROPS: ", props)
      let sliderThumbnail: Swiper;
      let slider: Swiper;

      onMounted(() => {
        Swiper.use([Navigation, Pagination, Scrollbar, Thumbs]);

        sliderThumbnail = new Swiper('.slider-thumbnail', {
          slidesPerView: 4,
          spaceBetween: 16,
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
        });

        slider = new Swiper('.slider', {
          slidesPerView: 1,
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          },
          thumbs: {
            swiper: sliderThumbnail
          },
        });

        sliderThumbnail.init();
        slider.init();
      })
    },
    methods: {
      formatDate(value) {
        return new Date(value).toLocaleString('en-us', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      }
    }
  };
</script>
